import EventOutlined from '@mui/icons-material/EventOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import { Box, Stack, Typography } from '@mui/material';
import {
  GuestJourneySettingsHistoryEntry,
  GuestJourneyStageSnapshotEntry,
} from 'api/reports-service/guest-journey-reports.types';
import { stageRowChanged } from 'api/reports-service/guest-journey-settings.utils';
import IncDrawer from 'components/drawers/inc-drawer/inc-drawer.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export type GuestJourneySettingsChangesDrawerProps = ReturnType<
  typeof useDrawer<GuestJourneySettingsHistoryEntry>
>;

/**
 * The per-row "Threshold Changes" drawer opened from the Settings History eye
 * button. Shows the before/after settings snapshots for one revision side by
 * side, highlighting the rows (tier name or range) that changed.
 */
const GuestJourneySettingsChangesDrawer = (
  drawer: GuestJourneySettingsChangesDrawerProps,
) => {
  const { t } = useTranslation();
  const entry = drawer.data;

  return (
    <IncDrawer
      {...drawer}
      title={t('page.guestJourney.settings.changes.title')}
      subtitle={t('page.guestJourney.settings.changes.eyebrow')}
      data-testid="GuestJourney.Settings.ChangesDrawer"
    >
      {entry && (
        <Stack spacing={3} paddingX={3} paddingY={2}>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EventOutlined fontSize="small" color="action" />
              <Typography variant="body2">
                {DateTime.fromISO(entry.changedAt)
                  .toUTC()
                  .toFormat('yyyy-MM-dd HH:mm:ss')}
              </Typography>
            </Stack>
            {entry.changedByName && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutline fontSize="small" color="action" />
                <Typography variant="body2">{entry.changedByName}</Typography>
              </Stack>
            )}
          </Stack>

          <SettingsChangeColumns before={entry.before} after={entry.after} />
        </Stack>
      )}
    </IncDrawer>
  );
};

type ChangedFlags = { visits: boolean[]; spend: boolean[] };

/** A snapshot reduced to the diff drawer's two row groups. */
type SnapshotRows = {
  visits: GuestJourneyStageSnapshotEntry[];
  spend: GuestJourneyStageSnapshotEntry[];
};

type SettingsChangeColumnsProps = {
  before: GuestJourneySettingsHistoryEntry['before'];
  after: GuestJourneySettingsHistoryEntry['after'];
};

const SettingsChangeColumns = ({
  before,
  after,
}: SettingsChangeColumnsProps) => {
  const { t } = useTranslation();
  // The BE snapshots are already display-ready rows (name + formatted range).
  const beforeRows: SnapshotRows = {
    visits: before.visitStages,
    spend: before.spendStages,
  };
  const afterRows: SnapshotRows = {
    visits: after.visitStages,
    spend: after.spendStages,
  };

  const changed: ChangedFlags = {
    visits: beforeRows.visits.map((row, i) =>
      stageRowChanged(row, afterRows.visits[i]),
    ),
    spend: beforeRows.spend.map((row, i) =>
      stageRowChanged(row, afterRows.spend[i]),
    ),
  };

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
      <SnapshotColumn
        side="before"
        heading={t('page.guestJourney.settings.changes.before')}
        rows={beforeRows}
        changed={changed}
      />
      <SnapshotColumn
        side="after"
        heading={t('page.guestJourney.settings.changes.after')}
        rows={afterRows}
        changed={changed}
      />
    </Box>
  );
};

type SnapshotColumnProps = {
  side: 'before' | 'after';
  heading: string;
  rows: SnapshotRows;
  changed: ChangedFlags;
};

const SnapshotColumn = ({
  side,
  heading,
  rows,
  changed,
}: SnapshotColumnProps) => {
  const { t } = useTranslation();
  const groups = [
    {
      key: 'visits' as const,
      title: t('page.guestJourney.settings.defaults.visitStages'),
      rows: rows.visits,
      changed: changed.visits,
    },
    {
      key: 'spend' as const,
      title: t('page.guestJourney.settings.defaults.averageCheckStages'),
      rows: rows.spend,
      changed: changed.spend,
    },
  ];

  return (
    <Stack
      spacing={2}
      data-testid={`GuestJourney.Settings.ChangesDrawer.${side}`}
    >
      <Typography variant="subtitle2">{heading}</Typography>
      {groups.map(group => (
        <Stack key={group.key} spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {group.title}
          </Typography>
          {group.rows.map((row, i) => (
            <Stack
              key={row.name + i}
              direction="row"
              justifyContent="space-between"
              spacing={1}
              data-changed={group.changed[i] ? 'true' : undefined}
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                ...(group.changed[i] && {
                  backgroundColor: 'warning.light',
                  // The highlight stays a light color in both themes, so pin the
                  // text to a contrasting ink rather than letting it inherit the
                  // theme's (light-on-light → invisible in dark mode).
                  color: theme =>
                    theme.palette.getContrastText(theme.palette.warning.light),
                }),
              }}
            >
              <Typography variant="body2">{row.name}</Typography>
              <Typography variant="body2" fontWeight={700}>
                {row.range}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

export default GuestJourneySettingsChangesDrawer;
